import { TimePicker } from "@mui/lab";
import { Button, Grid, TextField } from "@mui/material";
import { updateShift } from "api/services/shift";
import DrawerWrapper from "components/DrawerWrapper";
import { snack } from "components/toast";
import moment from "moment";
import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "react-query";

const defaultState = () => ({
    name: "",
    startTime: "09:00",
    endTime: "18:00",
    halfTime: "13:30",
    tagline: "",
});

const toDate = (t: string) => moment(t, "HH:mm").toDate();
const fromDate = (d: Date | null) => (d ? moment(d).format("HH:mm") : "");

const toMins = (t: string) => {
    const [h, m] = t.split(":").map(Number);
    return h * 60 + m;
};

// Adds minsToAdd to a "HH:mm" time, wrapping past midnight (e.g. 22:00 + 8h -> 06:00) so it
// works the same for a same-day shift and one that crosses into the next day.
const addMinutesToTime = (time: string, minsToAdd: number): string => {
    const wrapped = ((toMins(time) + minsToAdd) % (24 * 60) + 24 * 60) % (24 * 60);
    const h = Math.floor(wrapped / 60);
    const m = wrapped % 60;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

function ShiftDialog({ open, setOpen, data, stdHours, stdMinutes }: {
    open: boolean;
    setOpen: (v: boolean) => void;
    data?: any;
    stdHours?: any;
    stdMinutes?: any;
}) {
    const queryClient = useQueryClient();
    const [state, setState] = useState(defaultState());

    useEffect(() => {
        if (open) {
            setState(
                data
                    ? {
                          name: data.name || "",
                          startTime: data.inTime || "09:00",
                          endTime: data.outTime || "18:00",
                          halfTime: data.firstHalfOutTime || data.secondHalfInTime || "13:30",
                          tagline: data.tagline || "",
                      }
                    : defaultState()
            );
        }
    }, [open, data]);

    // End time isn't user-editable (the field is disabled below) — it's always derived from
    // start time + the org's Standard Working Hours, recomputed whenever either changes.
    useEffect(() => {
        if (!open || !stdHours || !stdMinutes) return;
        const stdMins = (parseInt(stdHours.value ?? 0) || 0) * 60 + (parseInt(stdMinutes.value ?? 0) || 0);
        setState((p) => ({ ...p, endTime: addMinutesToTime(p.startTime, stdMins) }));
    }, [open, state.startTime, stdHours, stdMinutes]);

    const { mutate: update, isLoading: updating } = useMutation(updateShift, {
        onSuccess: () => {
            snack.success("Shift Updated. This Shift Updates will be applicable from Next day");
            queryClient.invalidateQueries("shifts");
            setOpen(false);
        },
        onError: (err: any) => {
            snack.error(err?.response?.data?.message || "Something went wrong");
        },
    });

    // The shift name can't be edited here (it's disabled above), so it's a reliable signal for
    // whether this shift is expected to cross midnight — unlike inferring it purely from
    // end/half being numerically before start, which would also silently "accept" a genuine
    // start/end typo on a same-day shift as if it were an overnight one.
    const isNightShift = /night/i.test(data?.name || "");

    const handleSave = () => {
        if (!state.name.trim()) return snack.error("Shift name is required");
        if (!state.startTime) return snack.error("Start time is required");
        if (!state.endTime) return snack.error("End time is required");
        if (!state.halfTime) return snack.error("Half day switch time is required");
        if (state.endTime === state.startTime) return snack.error("End time must be different from start time");
        if (state.halfTime === state.startTime) return snack.error("Half day time must be after start time");
        if (state.halfTime === state.endTime) return snack.error("Half day time must be before end time");

        if (!isNightShift) {
            // Same-day shift: end/half before start is a mistake, not an overnight shift.
            if (state.endTime < state.startTime) return snack.error("End time must be after start time");
            if (state.halfTime < state.startTime) return snack.error("Half day time must be after start time");
            if (state.halfTime > state.endTime) return snack.error("Half day time must be before end time");
        }

        // Night shift: normalize end/half onto a continuous timeline starting at startMins
        // (adding 24h to whichever one is numerically before start, i.e. falls on the next
        // day) so the duration math below works the same as it would for a same-day shift.
        const startMins = toMins(state.startTime);
        const rawEndMins = toMins(state.endTime);
        const endMins = rawEndMins < startMins ? rawEndMins + 24 * 60 : rawEndMins;
        const rawHalfMins = toMins(state.halfTime);
        const halfMins = rawHalfMins < startMins ? rawHalfMins + 24 * 60 : rawHalfMins;

        if (isNightShift && halfMins > endMins) {
            return snack.error("Half day time must be between start and end time");
        }

        if (stdHours && stdMinutes) {
            const shiftMins = endMins - startMins;
            const stdMins = (parseInt(stdHours.value ?? 0) * 60) + parseInt(stdMinutes.value ?? 0);
            if (shiftMins !== stdMins) {
                snack.error(`Shift duration must equal Standard Working Hours (${stdHours.label}h ${stdMinutes.label}m)`);
                return;
            }
        }

        update({
            id: data.id,
            data: {
                name: state.name.trim(),
                inTime: state.startTime,
                outTime: state.endTime,
                firstHalfOutTime: state.halfTime,
                secondHalfInTime: state.halfTime,
                tagline: state.tagline.trim() || null,
            },
        });
    };

    return (
        <DrawerWrapper open={open} setOpen={setOpen} title="Edit Shift">
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <TextField
                        label="Shift Name"
                        fullWidth
                        size="small"
                        required
                        value={state.name}
                        onChange={(e) => setState((p) => ({ ...p, name: e.target.value }))}
                        placeholder="e.g. Morning Shift"
                        inputProps={{ maxLength: 50 }}
                        disabled
                    />
                </Grid>

                {/* Full day times — display only */}
                <Grid item xs={6}>
                    <TimePicker
                        label="Start Time"
                        value={toDate(state.startTime)}
                        onChange={() => {}}
                        ampm={false}
                        disabled
                        renderInput={(params) => (
                            <TextField {...params} fullWidth size="small" required InputLabelProps={{ shrink: true }} />
                        )}
                    />
                </Grid>
                <Grid item xs={6}>
                    <TimePicker
                        label="End Time"
                        value={toDate(state.endTime)}
                        onChange={() => {}}
                        ampm={false}
                        disabled
                        renderInput={(params) => (
                            <TextField {...params} fullWidth size="small" required InputLabelProps={{ shrink: true }} />
                        )}
                    />
                </Grid>

                {/* First half */}
                <Grid item xs={6}>
                    <TimePicker
                        label="First Half Start Time"
                        value={toDate(state.startTime)}
                        onChange={(v) => { const t = fromDate(v); if (t) setState((p) => ({ ...p, startTime: t })); }}
                        ampm={false}
                        renderInput={(params) => (
                            <TextField {...params} fullWidth size="small" required InputLabelProps={{ shrink: true }} />
                        )}
                    />
                </Grid>
                <Grid item xs={6}>
                    <TimePicker
                        label="First Half End Time"
                        value={toDate(state.halfTime)}
                        onChange={(v) => { const t = fromDate(v); if (t) setState((p) => ({ ...p, halfTime: t })); }}
                        ampm={false}
                        renderInput={(params) => (
                            <TextField {...params} fullWidth size="small" required InputLabelProps={{ shrink: true }} />
                        )}
                    />
                </Grid>

                {/* Second half */}
                <Grid item xs={6}>
                    <TimePicker
                        label="Second Half Start Time"
                        value={toDate(state.halfTime)}
                        onChange={() => {}}
                        ampm={false}
                        disabled
                        renderInput={(params) => (
                            <TextField {...params} fullWidth size="small" required InputLabelProps={{ shrink: true }} helperText="Auto-calculated from First Half End Time"/>
                        )}
                    />
                </Grid>
                <Grid item xs={6}>
                    <TimePicker
                        label="Second Half End Time"
                        value={toDate(state.endTime)}
                        onChange={() => {}}
                        ampm={false}
                        disabled
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                fullWidth
                                size="small"
                                required
                                InputLabelProps={{ shrink: true }}
                                helperText="Auto-calculated from (First Half Start Time + Standard Working Hours)"
                            />
                        )}
                    />
                </Grid>

                <Grid item xs={12}>
                    <TextField
                        label="Tagline"
                        fullWidth
                        size="small"
                        value={state.tagline}
                        onChange={(e) => setState((p) => ({ ...p, tagline: e.target.value }))}
                        placeholder="e.g. US clients, remote contractor"
                        inputProps={{ maxLength: 100 }}
                    />
                </Grid>
            </Grid>

            <Button
                fullWidth
                variant="contained"
                onClick={handleSave}
                disabled={updating}
                sx={{ mt: 3, bgcolor: "#1a2e4a", "&:hover": { bgcolor: "#263d5c" }, textTransform: "none", borderRadius: "8px" }}
            >
                {updating ? "Saving..." : "Save Changes"}
            </Button>
        </DrawerWrapper>
    );
}

export default ShiftDialog;
